import {
  type CanvasElementFrom,
  type HeightSettings,
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type StyleConfigProps,
  type VisibilityElementProps,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type BarcodeScannerFormat,
  type BorderRadiusValue,
  type CanvasElementPropertiesWithData,
  type ElementLabelPropsWithAdvancedAlign,
  type ElementLabelStyleProps,
  type ElementSizingMode,
  type FontOptions,
  getActiveBinding,
  type PropertiesConfig,
  type PropertiesConfigInputColorModel,
  type PropertiesConfigInputSpacingAndAlignmentModel,
} from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { FormLikeFieldValidationHandler } from '../../canvas-elements/io-data/default-handlers/formlike-field-validation-handler.service';
import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';
import { TextInputOutputEmitGuard } from '../text-input/text-input-output-emit.guard';

import { BarcodeScannerComponent } from './barcode-scanner.component';
import { BarcodeScannerPropsBuilder } from './barcode-scanner.props-builder';
import { barcodeScannerV2Migration } from './migrations/002-barcode-scanner.migration';
import { barcodeScannerV3Migration } from './migrations/003-barcode-scanner.migration';
import { barcodeScannerV4Migration } from './migrations/004-barcode-scanner.migration';
import { barcodeScannerV5PaddingToMarginMigration } from './migrations/005-barcode-scanner-padding-to-margin.migration';

export type BarcodeScannerElement = CanvasElementFrom<'barcodeScanner'>;
export type BarcodeScannerDef = WithProps<BarcodeScannerPropsModel> & WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    barcodeScanner: BarcodeScannerDef;
  }
}

const DEFAULT_WIDTH = 356;
const DEFAULT_HEIGHT = 40;
const DEFAULT_HEIGHT_LABEL_TOP = 72;
const MIN_SIZE = 40;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['fixed', 'auto'];

registerElementDefinition({
  componentType: BarcodeScannerComponent,
  outputEmitGuard: TextInputOutputEmitGuard,
  validationHandler: FormLikeFieldValidationHandler,
  name: 'Barcode Scanner',
  type: 'barcodeScanner',
  isListed: true,
  width: { mode: 'value', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: false },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitial(),
  propsBuilder: BarcodeScannerPropsBuilder,
  tags: ['barcode', 'qr', 'code', 'scanner'],
  groups: ['advanced'],
  styleIncludePaths: [
    'style',
    'content.title.align',
    'content.title.iconPosition',
    'content.title.position',
    'content.title.width',
  ],
  metaUiFeatures: {
    type: 'input',
    paths: {
      format: 'content.input.format',
      placeholder: 'content.input.placeholder',
    },
  },
  version: 5,
  migrations: {
    2: barcodeScannerV2Migration,
    3: barcodeScannerV3Migration,
    4: barcodeScannerV4Migration,
    5: barcodeScannerV5PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [{ property: 'value', label: 'Input value', dataType: 'String' }],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [
      {
        eventType: 'change',
        schema: {
          // While the default data type is a String, this can change later to a Number if a format of the filed changes
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              name: '', // for non-object value schema, this should be empty
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Barcode Scanner',
        },
      },
      {
        eventType: 'submit',
        schema: {
          // Sends formId of the form it's in.
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              name: '', // for non-object value schema, this should be empty
              isProfilePicture: false,
            },
          ],
          id: '',
          name: 'Barcode Scanner',
        },
      },
    ],
  };
}

type PropsModel = CanvasElementPropertiesWithData & StyleConfigProps & VisibilityElementProps;

export type BarcodeScannerPropsModel = PropsModel & {
  content: {
    title: ElementLabelPropsWithAdvancedAlign;
    input: {
      placeholder: string;
      format: BarcodeScannerFormat;
    };
    container: {
      overflow: string;
    };
  };
  style: {
    fontSize: ValueWithUnitOfMeasure;
    fontFamily: string;
    lineHeight: ValueWithUnitOfMeasure;
    general: {
      title: PropertiesConfigInputColorModel;
      offset: PropertiesConfigInputSpacingAndAlignmentModel;
    };
    labelBlock: ElementLabelStyleProps;
    inputFont: FontOptions;
    input: {
      accent: PropertiesConfigInputColorModel;
      border: PropertiesConfigInputColorModel;
      borderWidth: ValueWithUnitOfMeasure;
      fill: PropertiesConfigInputColorModel;
      radius: BorderRadiusValue;
      value: PropertiesConfigInputColorModel;
    };
  };
};

function getDefaults(): PropsOf<'barcodeScanner'> {
  return {
    content: {
      title: {
        label: 'Barcode Scanner',
        position: 'left',
        align: { horizontal: 'left' },
        width: '30%',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
      input: {
        placeholder: 'Scan item',
        format: 'text',
      },
      container: {
        overflow: 'auto',
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        offset: { margin: '$t.spacingInput' },
        title: { color1: '$t.title', selectorType: 'solid' },
      },
      labelBlock: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontStyle: [],
        fontWeight: '$t.labelFontWeight',
        iconSize: '16px',
        lineHeight: '$t.lineHeight',
      },
      inputFont: {
        fontFamily: '$t.fontFamily',
        fontSize: '$t.fontSize',
        fontWeight: '$t.fontWeight' as FontWeightOption,
        lineHeight: '$t.lineHeight',
        style: [],
      },
      input: {
        accent: { color1: '$t.accent', selectorType: 'solid' },
        border: { color1: '$t.outline', selectorType: 'solid' },
        borderWidth: '1px',
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        radius: '$t.radius',
        value: { color1: '$t.value', selectorType: 'solid' },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'barcodeScanner'> {
  return {
    content: {
      title: {
        width: '35%',
      },
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('barcodeScanner', [
    epc.section('content', 'Content', [
      epc.subsections.subsection(
        'content.input',
        'Input',
        [
          epc.utils.conditionalField(
            epc.utils.observedField(
              epc.fields.barcodeScannerFormatField('content.input.format', 'Format'),
              (model: BarcodeScannerPropsModel, format: BarcodeScannerFormat | undefined) => {
                if (model.content.input.format === format) {
                  return {};
                }
                return { 'root.height.fixed': format === 'paragraph', 'root.height.mode': 'value' };
              },
            ),
            'content.input.format',
            (_, model) => {
              return (
                getActiveBinding(model.data)?.dataSource?.columns?.[0]?.format?.formatType ===
                'SecureText'
              );
            },
          ),
          epc.fields.textInput('content.input.placeholder', 'Placeholder'),
        ],
        {
          dataBindings: [
            {
              key: 'value',
              label: 'Value',
              sources: 'field',
              bindings: [{ property: 'value', label: 'Input value', dataType: 'String' }],
            },
          ],
        },
      ),

      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
          (model, options) => {
            const format = getValueByPath(model, 'content.input.format') as
              | BarcodeScannerFormat
              | undefined;
            return format === 'paragraph' ? options : [];
          },
        ),
        epc.utils.conditionalField(
          epc.fields.contentOverflowField('content.container.overflow', 'Text behavior'),
          'content.input.format',
          (format, model) => {
            const isNotParagraph = (format as BarcodeScannerFormat | undefined) !== 'paragraph';
            const isNotFixed =
              (getValueByPath(model, 'root.height') as HeightSettings)?.fixed === false;
            return isNotParagraph || isNotFixed;
          },
        ),
      ]),

      epc.subsections.visibilitySubsection(),

      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.input', 'Barcode Field Style', [
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
          epc.fields.colorField('style.input.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.inputFont', 'Barcode Value', [
          epc.fields.colorField('style.input.value', 'Label color', {
            allowGradient: false,
            allowNone: false,
          }),
          ...epc.fields.fontFamilyAndWeightFields(
            'style.inputFont.fontFamily',
            'style.inputFont.fontWeight',
          ),
          epc.fields.fontStyleField('style.inputFont.style', 'Style', ['bold']),
          epc.fields.sliderField('style.inputFont.fontSize', 'Size', [
            { min: 8, max: 72, unit: 'px' },
          ]),
          epc.utils.conditionalField(
            epc.fields.sliderField('style.inputFont.lineHeight', 'Line height', [
              { min: 8, max: 72, unit: 'px' },
            ]),
            'content.input.format',
            (format: BarcodeScannerFormat | undefined) => format !== 'paragraph',
          ),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
            overrides: {
              horizontalAlign: epc.fields.alignField('content.title.align', 'Align', {
                defaultValue: { horizontal: 'left', vertical: 'middle' },
                excludedOptions: ['center'],
                vertical: (model) =>
                  getValueByPath(model, 'content.title.position') === 'left' &&
                  (getValueByPath(model, 'content.input.format') as
                    | BarcodeScannerFormat
                    | undefined) === 'paragraph',
              }),
              position: epc.fields.inputTitlePositionField(
                'content.title.position',
                DEFAULT_HEIGHT,
                DEFAULT_HEIGHT_LABEL_TOP,
                'style.general.offset.margin',
              ),
            },
          },
        ),
      ],
      true,
    ),
  ]);
}
